// ============================================================================
// ProductionControlRoom_InfoPanels.tsx
// Panneaux d'information : device distant et négociation WHIP
// ============================================================================

import React, { useState } from 'react';
import { RemoteDeviceInfo, WhipNegotiationInfo } from '../../../dataTypes/liveStreamManagment/types_webrtc';
import { profileName } from '../../../dataTypes/liveStreamManagment/utils_codec';
import {
  PanelOverlay, PanelHeader, InfoRow, Section,
} from './ProductionControlRoom_SharedUI';

import { StatusDot } from './ProductionControlRoom_SharedUI';
import { SdpAccordion } from './SdpDebugPanel';

// RTCOutboundRtpStreamStats.qualityLimitationReason ('none' | 'cpu' | 'bandwidth' | 'other')
const QUALITY_LIMITATION_LABELS: Record<string, string> = {
  none:      'Aucune',
  bandwidth: 'Bande passante',
  cpu:       'CPU',
  other:     'Autre',
};

// ─── DeviceInfoPanel ──────────────────────────────────────────────────────────

export const DeviceInfoPanel: React.FC<{ info: RemoteDeviceInfo; onClose: () => void }> = ({ info, onClose }) => {
  const [tab, setTab] = useState<'device' | 'codec' | 'stats'>('device');

  return (
    <PanelOverlay onClose={onClose}>
      <div style={{
        background: '#0d1117', borderRadius: 16, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
      }}>
        <PanelHeader
          icon="📱"
          title={info.label || info.device_id}
          subtitle={`Device ID : ${info.device_id}`}
          onClose={onClose}
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, margin: '12px 16px 8px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3 }}>
          {([['device', '📱 Appareil'], ['codec', '🎥 Codecs'], ['stats', '📊 Stats']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: '7px 4px', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: tab === id ? 'rgba(99,102,241,0.85)' : 'transparent',
              color: tab === id ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>

          {tab === 'device' && (
            <>
              <Section title="Connexion WebRTC">
                <InfoRow
                  label="État connexion"
                  value={<><StatusDot state={info.connectionState} />{info.connectionState || '—'}</>}
                />
                <InfoRow label="État ICE" value={info.iceState || '—'} />
              </Section>
              <Section title="Plateforme">
                <InfoRow label="Plateforme"  value={info.platform || 'Non renseigné'} />
                <InfoRow label="User Agent"  value={info.userAgent ? info.userAgent.slice(0, 60) + '…' : '—'} />
              </Section>
              <Section title="Identifiants">
                <InfoRow label="Device ID" value={info.device_id} accent="purple" />
                <InfoRow label="Label"     value={info.label} />
              </Section>
            </>
          )}

          {tab === 'codec' && (
            <>
              <Section title="Codec vidéo négocié (device ↔ régie)">
                <InfoRow
                  label="Codec"
                  value={info.negotiatedVideoCodec || '—'}
                  accent={info.negotiatedVideoCodec ? 'green' : undefined}
                />
                <InfoRow
                  label="Profil H264"
                  value={info.negotiatedVideoProfile
                    ? `${profileName(info.negotiatedVideoProfile)} (${info.negotiatedVideoProfile})`
                    : '—'}
                  accent={
                    info.negotiatedVideoProfile?.toLowerCase().startsWith('42e0') ? 'green' :
                    info.negotiatedVideoProfile ? 'orange' : undefined
                  }
                />
                {info.negotiatedVideoProfile?.toLowerCase().startsWith('640') && (
                  <div style={{ marginTop: 6, padding: '6px 8px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, fontSize: 11, color: '#fbbf24' }}>
                    ⚠️ Profil High — un switch vers un device iOS peut nécessiter une renégociation WHIP
                  </div>
                )}
                <InfoRow label="Codec audio" value={info.negotiatedAudioCodec || '—'} />
              </Section>
              <SdpAccordion title="SDP Offer (device → régie)"  sdp={info.lastOfferSdp} />
              <SdpAccordion title="SDP Answer (régie → device)" sdp={info.lastAnswerSdp} />
            </>
          )}

          {tab === 'stats' && (
            <>
              <Section title="Données reçues">
                <InfoRow
                  label="Bytes reçus"
                  value={info.bytesReceived ? `${(info.bytesReceived / 1024 / 1024).toFixed(2)} MB` : '—'}
                  accent="blue"
                />
                <InfoRow label="Paquets reçus" value={info.packetsReceived?.toString() || '—'} />
              </Section>
              <Section title="Note">
                <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.5 }}>
                  Les stats complètes (bitrate, FPS, perte de paquets) sont disponibles dans les DevTools
                  du device distant → onglet "Infos" → "Codecs".
                </p>
              </Section>
            </>
          )}
        </div>
      </div>
    </PanelOverlay>
  );
};

// ─── WhipInfoPanel ────────────────────────────────────────────────────────────

export const WhipInfoPanel: React.FC<{
  info:              WhipNegotiationInfo;
  activeSourceLabel: string;
  onClose:           () => void;
  onRenegotiate:     () => void;
}> = ({ info, activeSourceLabel, onClose, onRenegotiate }) => {
  const [tab, setTab] = useState<'codec' | 'stats' | 'sdp'>('codec');

  return (
    <PanelOverlay onClose={onClose}>
      <div style={{
        background: '#0d1117', borderRadius: 16, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
      }}>
        <PanelHeader
          icon="📡"
          title="Négociation WHIP → MediaMTX"
          subtitle={`Source active : ${activeSourceLabel}`}
          onClose={onClose}
        />

        <div style={{ display: 'flex', gap: 0, margin: '12px 16px 8px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3 }}>
          {([['codec', '🎥 Codecs'], ['stats', '📊 Stats'], ['sdp', '📋 SDP']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: '7px 4px', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: tab === id ? 'rgba(16,185,129,0.75)' : 'transparent',
              color: tab === id ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>

          {tab === 'codec' && (
            <>
              <Section title="État PeerConnection WHIP">
                <InfoRow
                  label="État connexion"
                  value={<><StatusDot state={info.state} />{info.state || '—'}</>}
                />
                <InfoRow label="ICE" value={info.iceState || '—'} />
              </Section>
              <Section title="Codecs négociés (régie → MediaMTX)">
                <InfoRow
                  label="Codec vidéo"
                  value={info.videoCodec || '—'}
                  accent={info.videoCodec ? 'green' : undefined}
                />
                <InfoRow
                  label="Profil H264"
                  value={info.videoProfile
                    ? `${profileName(info.videoProfile)} (${info.videoProfile})`
                    : '—'}
                  accent={
                    info.videoProfile?.toLowerCase().startsWith('42e0') ? 'green' :
                    info.videoProfile?.toLowerCase().startsWith('4d00') ? 'blue'  :
                    info.videoProfile ? 'orange' : undefined
                  }
                />
                <InfoRow label="Codec audio"  value={info.audioCodec  || '—'} />
                <InfoRow label="Payload Type" value={info.payloadType || '—'} />
              </Section>
              <Section title="Cohérence avec le device actif">
                <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
                  Si vous changez de source (ex: Android → iOS), le profil codec peut changer.<br />
                  Un <strong style={{ color: '#fbbf24' }}>replaceTrack()</strong> seul ne renégocie pas
                  les codecs avec MediaMTX.<br />
                  Utilisez le bouton ci-dessous pour forcer une renégociation complète.
                </p>
              </Section>
              <button
                onClick={() => { onRenegotiate(); onClose(); }}
                style={{
                  marginTop: 4, width: '100%', padding: '10px',
                  border: '1px solid rgba(251,191,36,0.4)',
                  borderRadius: 8, background: 'rgba(251,191,36,0.1)', color: '#fbbf24',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                🔄 Forcer la renégociation WHIP (recrée la PeerConnection)
              </button>
            </>
          )}

          {tab === 'stats' && (
            <>
              <Section title="Débit vers MediaMTX">
                <InfoRow
                  label="Bytes envoyés"
                  value={info.bytesSent ? `${(info.bytesSent / 1024 / 1024).toFixed(2)} MB` : '—'}
                  accent="green"
                />
                <InfoRow label="Paquets envoyés" value={info.packetsSent?.toString() || '—'} />
                <InfoRow label="Bitrate estimé"  value={info.bitratKbps ? `${info.bitratKbps.toFixed(0)} kbps` : '—'} accent="blue" />
                <InfoRow label="Résolution"      value={info.resolution || '—'} />
                <InfoRow label="FPS"             value={info.fps ? `${info.fps.toFixed(1)} fps` : '—'} />
              </Section>
              <Section title="Lien régie → MediaMTX">
                <InfoRow
                  label="Bande passante dispo"
                  value={info.availableOutgoingKbps ? `${info.availableOutgoingKbps.toFixed(0)} kbps` : '—'}
                  accent="blue"
                />
                <InfoRow
                  label="Aller-retour"
                  value={info.rttMs ? `${info.rttMs.toFixed(0)} ms` : '—'}
                  accent={info.rttMs && info.rttMs > 150 ? 'orange' : undefined}
                />
                {/* La marge est ce qui décide si la machine peut porter des angles en plus
                    du programme : chaque angle est un flux montant supplémentaire, et les
                    PeerConnections ne se coordonnent pas entre elles. */}
                <InfoRow
                  label="Marge"
                  value={
                    info.availableOutgoingKbps && info.bitratKbps
                      ? `${(info.availableOutgoingKbps - info.bitratKbps).toFixed(0)} kbps libres`
                      : '—'
                  }
                  accent={
                    info.availableOutgoingKbps && info.bitratKbps
                      ? (info.availableOutgoingKbps - info.bitratKbps < 1000 ? 'red'
                        : info.availableOutgoingKbps - info.bitratKbps < 3000 ? 'orange'
                        : 'green')
                      : undefined
                  }
                />
              </Section>
              <Section title="Congestion réseau">
                <InfoRow
                  label="Limitation qualité"
                  value={QUALITY_LIMITATION_LABELS[info.qualityLimitationReason || 'none'] || info.qualityLimitationReason || '—'}
                  accent={
                    info.qualityLimitationReason === 'bandwidth' ? 'red' :
                    info.qualityLimitationReason === 'cpu'       ? 'orange' :
                    info.qualityLimitationReason === 'other'     ? 'orange' :
                    'green'
                  }
                />
                <InfoRow
                  label="Paquets perdus"
                  value={info.packetsLost?.toString() ?? '—'}
                  accent={info.packetsLost ? 'red' : undefined}
                />
                <InfoRow
                  label="NACK reçus"
                  value={info.nackCount?.toString() ?? '—'}
                  accent={info.nackCount ? 'orange' : undefined}
                />
              </Section>
              {info.qualityLimitationReason === 'bandwidth' && (
                <div style={{ marginTop: 4, padding: '6px 8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, fontSize: 11, color: '#f87171' }}>
                  ⚠️ Le réseau ne suit pas le débit configuré — la résolution reste figée à 1080p (maintain-resolution), la qualité et/ou le framerate sont sacrifiés à la place.
                </div>
              )}
            </>
          )}

          {tab === 'sdp' && (
            !info.offerSdp && !info.answerSdp ? (
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 20 }}>
                Aucune négociation WHIP active
              </p>
            ) : (
              <>
                <SdpAccordion title="SDP Offer envoyée à MediaMTX" sdp={info.offerSdp} />
                <SdpAccordion title="SDP Answer reçue de MediaMTX" sdp={info.answerSdp} />
              </>
            )
          )}
        </div>
      </div>
    </PanelOverlay>
  );
};
